import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, interval, Observable, Subscription} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {compareVersions} from 'compare-versions';
import {AskDialogComponent} from '../swiss-forms/ask-dialog/ask-dialog.component';
import {LocalStorageService} from '../local-storage.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {UserAuthenticationService} from './user-authentication.service';
import {HelperService} from './helper.service';
import {SwUpdate} from '@angular/service-worker';
import {environment} from '../../environments/environment';
import {DialogYesNo} from '../models/dialog-models';
import {BackupService} from './backup.service';
import {MessageBoxService} from './message-box.service';

@Injectable({
    providedIn: 'root'
})
export class VinavonUpdateService {
    private backUpService = inject(BackupService);
    private messageService = inject(MessageBoxService);
    public vinavonVersion: string = environment.version;
    public updateFound: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public newVersionFound: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private userAuthService: UserAuthenticationService,
        private helperService: HelperService,
        private localStorage: LocalStorageService,
        private http: HttpClient,
        private dialog: MatDialog,
        private swUpdate: SwUpdate) {
    }

    public startCheckUpdateInterval(): Subscription {
        this.checkForUpdate();
        return interval(30000).subscribe(() => {
            this.checkForUpdate();
        });
    }

    public askUserToDoUpdate(): Promise<boolean> {
        return new Promise((resolve) => {
            if (!this.updateFound.getValue()) {
                resolve(true);
            }

            const dialogOKData: DialogYesNo = {
                title: 'MAIN_APP_DIALOG.UPDATE_NOW.update',
                titleReplace: [],
                question: 'MAIN_APP_DIALOG.UPDATE_NOW.updateNow',
                questionReplace: [],
                confirm: 'MAIN_APP_DIALOG.confirm',
                cancel: 'MAIN_APP_DIALOG.cancel',
                dontShowCancel: false,
                dontShowTitle: false
            };

            this.dialog.open(AskDialogComponent, {
                autoFocus: true,
                disableClose: true,
                closeOnNavigation: true,
                data: dialogOKData,
            }).afterClosed().subscribe(async data => {
                if (data === true) {
                    try {
                        this.helperService.lockApp();
                        if (!await firstValueFrom(this.backUpService.uploadBackupToServer(await this.backUpService.createBackupFile()))) {
                            this.messageService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_UPDATE_BACKUP.text', 'MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_UPDATE_BACKUP.title');
                            return;
                        }

                        const sw = await navigator.serviceWorker?.getRegistration();
                        if (sw) {
                            await sw?.update();
                            await this.helperService.updateTranslations();
                            document.location.reload();
                        } else {
                            await this.helperService.updateTranslations();
                            document.location.reload();
                        }
                    }
                    catch (err) {
                        this.messageService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_UPDATE.text', 'MAIN_APP_DIALOG.ERROR_DIALOG.ERROR_UPDATE.title');
                    }
                    finally {
                        this.helperService.unlockApp();
                    }
                }
            });
        });
    }

    async updateExistsOnWebserver(currVersion: string): Promise<string | undefined> {
        const currentVersion = await this.getLoadedWebserverVinavonVersion().catch(() => undefined);
        if (!currentVersion) {
            throw Error('not connected to internet');
        }
        const serverVersion = JSON.parse(currentVersion).version;
        const res = compareVersions(serverVersion, currVersion);

        if (res === 1) {
            return serverVersion;
        } else {
            return undefined;
        }
    }

    public getLoadedWebserverVinavonVersion(): Promise<string> {
        return new Promise((resolve, reject) => {
            this.getWebserverVinavonVersion().subscribe({
                next: async (res: any) => {
                    // Vinavon-Webserver-Version in Indexed-DB speichern
                    await this.localStorage.add('newestVersion', res, 'SwissWebMainDB', false, true);
                    resolve(res);
                }, error: () => {
                    reject();
                },
            });
        });
    }

    getWebserverVinavonVersion(): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'text/plain; charset=utf-8', Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        headers.set('ngsw-bypass', '1');
        const config = this.helperService.getSwissWebServiceConfig();
        const url = config.userUrl;
        return this.http.get<any>(url + '/SwissAppVersion?cache-bust=true&ngsw-bypass', {headers, responseType: 'text' as 'json'});
    }

    private async checkForUpdate(): Promise<void> {
        try {
            const newVersion = await this.updateExistsOnWebserver(this.vinavonVersion);
            this.helperService.setOnlineState(true);
            if (newVersion) {
                this.newVersionFound.next(true);
                if (environment.production && await this.swUpdate.checkForUpdate()) {
                    this.updateFound.next(true);
                }
            } else {
                await this.localStorage.add('version', environment.version, 'SwissWebMainDB', false, true);
            }
        } catch (ex) {
            console.error(ex);
            this.helperService.setOnlineState(false);
        }
    }

}
